import React, { useCallback } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { theme, useResponsive } from '@sg/garnish';

import { OrderAgain, OrdersList } from '@order/features/orders';
import { useTelemetry } from '@order/Telemetry';

import { ReorderOrderLineItem } from '../../../../components';
import { useLastInteractedStore } from '../../../../context/LastInteractedStore';
import { useReorderOrdersV2 } from '../../../ReorderScreen/hooks';
import { OrdersEmptyState, OrdersSeeMoreButton } from '../../components';

export const DigitalOrders = () => {
  const navigation = useNavigation();
  const { track } = useTelemetry();
  const { match } = useResponsive();

  // ─── Query ────────────────────────────────────────────────────────────

  const lastInteractedStore = useLastInteractedStore();

  const currentRestaurantId = lastInteractedStore?.restaurantId
    ? Number.parseInt(lastInteractedStore.restaurantId, 10)
    : undefined;

  const {
    orders,
    fetchInitialOrders,
    isFetchingInitialOrders,
    isFetchingMoreOrders,
    canFetchMoreOrders,
    mostRecentOrder,
    fetchMoreOrders,
  } = useReorderOrdersV2(currentRestaurantId);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToOrderStatusScreen = React.useCallback(
    (orderId: string) => {
      navigation.navigate('Modal', {
        screen: 'OrderStatus',
        params: { orderId },
      });
    },
    [navigation],
  );

  const handleViewDetails = useCallback(
    (orderId: string) => {
      navigateToOrderStatusScreen(orderId);
      track('order_status.view', { entryPoint: 'account.orders' });
    },
    [navigateToOrderStatusScreen, track],
  );

  // ─── Effects ─────────────────────────────────────────────────────────────────

  // Fetch orders on screen focus.
  useFocusEffect(fetchInitialOrders);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollView
      scrollEventThrottle={16}
      style={match([null, orders.length > 0 ? styles.containerBorder : null])}
      contentContainerStyle={styles.scrollView}
    >
      <OrdersList.Container style={styles.orderList}>
        {isFetchingInitialOrders ? (
          <View style={styles.loadingContainer}>
            <OrderAgain.LoadingPlaceholder />
          </View>
        ) : null}

        {!isFetchingInitialOrders && orders.length === 0 ? (
          <OrdersEmptyState />
        ) : null}

        {!isFetchingInitialOrders && orders.length > 0
          ? orders.map((order) => {
              const location =
                order.deliveryOrderDetail?.address?.name ??
                order.restaurant.name;

              return (
                <OrdersList.OrderContainer key={order.id}>
                  <OrdersList.OrderHeader
                    date={order.wantedTime}
                    location={location}
                    onViewDetails={handleViewDetails}
                  />

                  <OrdersList.OrderLineItemsContainer>
                    {order.lineItems.map((lineItem) => {
                      return (
                        <ReorderOrderLineItem
                          key={lineItem.id}
                          lineItem={lineItem}
                          orderDetails={order}
                          mostRecentOrder={mostRecentOrder}
                        />
                      );
                    })}
                  </OrdersList.OrderLineItemsContainer>
                </OrdersList.OrderContainer>
              );
            })
          : null}

        {canFetchMoreOrders ? (
          <OrdersSeeMoreButton
            isFetchingMoreOrders={isFetchingMoreOrders}
            fetchMoreOrders={fetchMoreOrders}
          />
        ) : null}
      </OrdersList.Container>
    </ScrollView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerBorder: {
    borderRadius: theme.radius.large,
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
    overflow: 'hidden',
  },
  scrollView: {
    flexGrow: 1,
  },
  orderList: {
    marginTop: 0,
    marginBottom: 0,
  },
  loadingContainer: {
    marginVertical: theme.spacing['4'],
  },
});
